import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface TimeOffPolicy {
  id: string;
  countryCode: string;
  name: string;
  kind: 'paid' | 'sick' | 'parental' | 'care' | 'unpaid' | 'special';
  accrualRule: Record<string, unknown>;
  createdAt: string;
}

export interface TimeOffRequest {
  id: string;
  employeeId: string;
  policyId: string;
  startDate: string;
  endDate: string;
  hoursPerDay: number | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'canceled';
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
}

export function usePolicies(country?: string) {
  return useQuery({
    queryKey: ['time-off', 'policies', country ?? 'all'],
    queryFn: async () => {
      const r = await api.get<{ policies: TimeOffPolicy[] }>('/api/time-off/policies', { params: { country } });
      return r.data.policies;
    },
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { countryCode: string; name: string; kind: TimeOffPolicy['kind']; accrualRule: Record<string, unknown> }) => {
      const r = await api.post<TimeOffPolicy>('/api/time-off/policies', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-off', 'policies'] }),
  });
}

export function useRequests(params: { status?: string; employeeId?: string } = {}) {
  return useQuery({
    queryKey: ['time-off', 'requests', params],
    queryFn: async () => {
      const r = await api.get<{ requests: TimeOffRequest[] }>('/api/time-off/requests', { params });
      return r.data.requests;
    },
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      employeeId: string;
      policyId: string;
      startDate: string;
      endDate: string;
      hoursPerDay?: number;
      reason?: string;
    }) => {
      const r = await api.post<TimeOffRequest>('/api/time-off/requests', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-off'] }),
  });
}

export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<TimeOffRequest>(`/api/time-off/requests/${id}/approve`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-off'] }),
  });
}

export function useRejectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const r = await api.post<TimeOffRequest>(`/api/time-off/requests/${id}/reject`, { reason });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-off'] }),
  });
}

export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<TimeOffRequest>(`/api/time-off/requests/${id}/cancel`);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['time-off'] }),
  });
}

// ── 有給 balance + 36協定 overtime (Phase 3) ──

export interface LeaveBalance {
  employeeId: string;
  grantedActiveDays: number;
  usedDays: number;
  balanceDays: number;
  grants: Array<{ id: string; grantedOn: string; days: number; expiresOn: string; tenureMonths: number; source: string }>;
}

export interface OvertimeViolation { code: string; message: string; severity: 'warning' | 'violation'; month?: number }
export interface OvertimeYear {
  employeeId: string;
  year: number;
  records: Array<{ id: string; month: number; overtimeHours: number; workedHours: number | null; note: string | null }>;
  monthlyViolations: OvertimeViolation[];
  yearlyViolations: OvertimeViolation[];
  totalOvertimeHours: number;
}

export function useLeaveBalance(employeeId: string | null, weeklyDays?: number) {
  return useQuery({
    queryKey: ['leave-balance', employeeId, weeklyDays],
    enabled: !!employeeId,
    queryFn: async () => {
      const q = weeklyDays ? `?weeklyDays=${weeklyDays}` : '';
      const r = await api.get<LeaveBalance>(`/api/time-off/leave-balance/${employeeId}${q}`);
      return r.data;
    },
  });
}

export function useAccrueLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string) => {
      const r = await api.post(`/api/time-off/leave-balance/${employeeId}/accrue`);
      return r.data;
    },
    onSuccess: (_d, employeeId) => qc.invalidateQueries({ queryKey: ['leave-balance', employeeId] }),
  });
}

export function useOvertimeYear(employeeId: string | null, year: number) {
  return useQuery({
    queryKey: ['overtime', employeeId, year],
    enabled: !!employeeId,
    queryFn: async () => {
      const r = await api.get<OvertimeYear>(`/api/time-off/overtime/${employeeId}?year=${year}`);
      return r.data;
    },
  });
}

export function useRecordOvertime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { employeeId: string; year: number; month: number; overtimeHours: number; workedHours?: number; note?: string }) => {
      const r = await api.post('/api/time-off/overtime', args);
      return r.data;
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['overtime', vars.employeeId] }),
  });
}
